import { useState } from 'react';
import { useAuth } from '../contexts/auth';
import CustomModal from '../components/CustomModal/CustomModal';

/**
 * Hook para manejar respuestas de API con Error, Logout, Expired y mensajes de sesión
 * 
 * Casos de uso:
 * - Expired: true → Muestra modal "Sesión Expirada", luego logout al cerrar
 * - Msg: 'MsgSessionTokenNoExiste' o 'MsgSessionExpired' → Muestra modal, luego logout
 * - Error: true + Logout: false → Muestra modal de error
 * - Error: false + Logout: true → Logout inmediato sin modal
 * - Error: true + Logout: true → Muestra modal, luego logout al cerrar
 * - Error: false + Logout: false → No hace nada (flujo normal)
 */
export const useApiResponseHandler = () => {
  const { signOut } = useAuth();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState({ Title: '', Message: '', SecondaryMessage: '' });
  const [shouldLogoutAfterClose, setShouldLogoutAfterClose] = useState(false);

  /**
   * Maneja la respuesta de la API verificando Error, Logout, Expired y mensajes de sesión
   * @param {Object} parsedResults - Objeto parseado de la respuesta API
   * @returns {boolean} - true si hay error o logout (detiene flujo), false si continúa normal
   */
  const handleApiResponse = (parsedResults) => {
    if (!parsedResults) return false;

    const hasError = parsedResults.Error === true;
    const hasLogout = parsedResults.Logout === true;
    const hasExpired = parsedResults.Expired === true;
    const isSessionExpired = parsedResults.Msg === 'MsgSessionTokenNoExiste' || 
                             parsedResults.Msg === 'MsgSessionExpired';

    // PRIORIDAD 1: Si hay Error=true, mostrar el error (con o sin logout)
    if (hasError) {
      try {
        // Intenta parsear el Msg que viene como JSON stringificado
        const errorData = JSON.parse(parsedResults.Msg);
        setErrorMessage({
          Title: errorData.Title || 'Error',
          Message: errorData.Message || parsedResults.Msg,
          SecondaryMessage: '' // Sin mensaje secundario para errores
        });
      } catch (e) {
        // Si no se puede parsear, usa el mensaje tal cual
        setErrorMessage({
          Title: 'Error en la Operación',
          Message: parsedResults.Msg || 'Ha ocurrido un error desconocido',
          SecondaryMessage: '' // Sin mensaje secundario para errores
        });
      }
      
      // Si también viene Logout=true y Error=true, marcar para hacer logout después de cerrar modal
      setShouldLogoutAfterClose(hasLogout);
      setShowErrorModal(true);
      return true; // Detiene ejecución
    }

    // PRIORIDAD 2: Sesión Expirada (solo si NO hay Error)
    if (hasExpired || isSessionExpired) {
      setErrorMessage({
        Title: 'Sesión finalizada!',
        Message: 'Por motivos de seguridad la sesión ha sido finalizada.',
        SecondaryMessage: 'Se requiere un nuevo inicio de sesión para continuar trabajando en el sistema!'
      });
      setShouldLogoutAfterClose(true);
      setShowErrorModal(true);
      return true; // Detiene ejecución
    }

    // PRIORIDAD 3: Logout sin error
    if (hasLogout) {
      setErrorMessage({
        Title: 'Sesión finalizada!',
        Message: 'Por motivos de seguridad la sesión ha sido finalizada.',
        SecondaryMessage: 'Se requiere un nuevo inicio de sesión para continuar trabajando en el sistema!'
      });
      setShouldLogoutAfterClose(true);
      setShowErrorModal(true);
      return true; // Detiene ejecución
    }
    
    // Caso 4: No hay error ni logout, continúa flujo normal
    return false;
  };

  /**
   * Cierra el modal de error y ejecuta logout si es necesario
   */
  const closeErrorModal = () => {
    setShowErrorModal(false);
    
    // Si debe hacer logout después de cerrar
    if (shouldLogoutAfterClose) {
      setShouldLogoutAfterClose(false);
      // Pequeño delay para que el modal se cierre visualmente
      setTimeout(() => {
        signOut();
      }, 300);
    }
  };

  /**
   * Renderiza el modal de error si está visible
   */
  const ErrorModal = () => {
    if (!showErrorModal) return null;

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999
      }}>
        <CustomModal
          type="errorMessageBD"
          title={errorMessage.Title}
          message={errorMessage.Message}
          secondaryMessage={errorMessage.SecondaryMessage}
          primaryButtonText={null}
          secondaryButtonText={null}
          tertiaryButtonText="Cerrar"
          onTertiaryClick={closeErrorModal}
          onClose={closeErrorModal}
        />
      </div>
    );
  };

  return {
    showErrorModal,
    errorMessage,
    handleApiResponse,
    closeErrorModal,
    ErrorModal
  };
};

